import { useCallback, useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { defineMessages } from "react-intl";
import classNames from "classnames";

import Button from "../Buttons/Button";
import Icon from "../Utils/Icon";
import { useFormatMessage } from "../Intl/Context";

import { hideAlert } from "../../reducers/alerts";
import { pushContainers, restoreContainersPosition } from "../../reducers/styling";
import { useResizeObserver } from "../Utils/useResizeObserver";

export const ALERT_TYPE_SUCCESS = "success";
export const ALERT_TYPE_ERROR = "danger";
export const ALERT_TYPE_INFO = "info";

const Alert = props => {
  const { hideIcon } = props;

  const dispatch = useDispatch();
  const type = useSelector(({ alert }) => alert?.type);
  const message = useSelector(({ alert }) => alert?.message);
  const onClick = useSelector(({ alert }) => alert?.onClick);

  useEffect(() => {
    !message && dispatch(restoreContainersPosition());
  }, [dispatch, message]);

  // Depending on the type of the alert message, we use a different variable to display
  // it in the right container, allowing assistive technologies to report the right kind
  // of message.
  const successMessage = type === ALERT_TYPE_SUCCESS ? message : null;
  const errorMessage = [ALERT_TYPE_ERROR, ALERT_TYPE_INFO].includes(type)
    ? message
    : null;

  const t = useFormatMessage();
  const closeLabel = t(intlMessages.close);

  const alertKey = message && [type, message].join(":");
  const [alertContainer, setAlertContainer] = useState();

  const refreshAlertContainersOffset = useCallback(() => {
    if (!alertContainer) return;

    // Alerts container, when hidden, has a 1px height due to its "sr-only" class, which
    // is inteded to make its content accessible to screen readers with the "alert" and
    // "status" roles. So we ignore the 1px height when pushing containers.
    const alertsContainerHeight =
      alertContainer.clientHeight <= 1 ? 0 : alertContainer.clientHeight;

    // FIXIT : This is the reason why the forms are re-rendered when an alert is shown,
    //         which makes the error messages disappear.
    dispatch(pushContainers(alertsContainerHeight));
  }, [alertContainer, dispatch]);

  const resizeObserver = useResizeObserver(refreshAlertContainersOffset);

  useEffect(() => {
    refreshAlertContainersOffset();

    window.addEventListener("resize", refreshAlertContainersOffset);
    if (alertContainer) resizeObserver.observe(alertContainer);

    return () => {
      window.removeEventListener("resize", refreshAlertContainersOffset);
      if (alertContainer) resizeObserver.unobserve(alertContainer);
    };
  }, [alertContainer, refreshAlertContainersOffset, resizeObserver]);

  const handleClick = e => {
    if (onClick) {
      onClick(e);
    } else {
      handleDismissClicked(e);
    }
  };

  const handleDismissClicked = useCallback(
    e => {
      e.preventDefault();
      e.stopPropagation();

      dispatch(hideAlert());
    },
    [dispatch]
  );

  const alertClassName = classNames("alert", "alert-box", "alert-dismissible", {
    [`alert-${type}`]: type,
    "sr-only": !message
  });

  const successMessageClassName = classNames("alert-message", "flex-1", {
    "sr-only": !successMessage
  });

  const errorMessageClassName = classNames("alert-message", "flex-1", {
    "sr-only": !errorMessage
  });

  const alertIconName = useMemo(() => {
    if (hideIcon || !message) return;

    switch (type) {
      case ALERT_TYPE_SUCCESS:
        return "icon-success";
      case ALERT_TYPE_ERROR:
        return "icon-error";
      case ALERT_TYPE_INFO:
        return "icon-infos-border";
    }
  }, [hideIcon, message, type]);

  const alertIconClassName = !hideIcon && classNames("alert-icon", alertIconName);

  const closeButtonClassName = classNames("alert-close", { "d-none": !message });

  return (
    <div className="alerts-container">
      <div
        key={alertKey} // This allows resetting the ref when a new alert is shown
        className={alertClassName}
        onClick={handleClick}
        ref={setAlertContainer}
        data-sticky-alert
      >
        {alertIconClassName && <Icon className={alertIconClassName} />}

        <div role="status" className={successMessageClassName}>
          {successMessage && (
            <p
              className="margin-bottom-none"
              dangerouslySetInnerHTML={{ __html: successMessage }}
            />
          )}
        </div>

        <div role="alert" className={errorMessageClassName}>
          {errorMessage && (
            <p
              className="margin-bottom-none"
              dangerouslySetInnerHTML={{ __html: errorMessage }}
            />
          )}
        </div>

        <Button
          type="button"
          className={closeButtonClassName}
          onClick={handleDismissClicked}
          iconClassName="icon-close"
          screenReaderLabel={closeLabel}
          data-dismiss="alert"
        />
      </div>
    </div>
  );
};

export default Alert;

export const intlMessages = defineMessages({
  close: {
    id: "errorMessage.close",
    defaultMessage: "Fermer"
  }
});
